import { Injectable, inject } from '@angular/core';

import type { TopicItem } from '@features/kt/guided-activity-wizard/models/nat-curriculum.model';
import { TopicsApiService } from '@features/kt/guided-activity-wizard/services/api/topics.api.service';

import type {
  ActivitiesListResponse,
  ActivityContentSection,
  ActivityCover,
  ActivityCoverGenerationResult,
  ActivityFieldLayoutItem,
  ActivityInstanceDetail,
  ActivityPedagogicalCustomFieldList,
  ActivityPedagogicalFieldList,
  ActivityPedagogicalFieldResponse,
  ActivityStickerGeneration,
  ActivityTemplateDetail,
  ActivityVisibility,
  AttachmentMode,
  CreateActivityPedagogicalCustomFieldRequest,
  GetTagsItem,
  PedagogicalFieldName,
  UpdateActivityCoverFramingRequest,
  UpdateActivityInstanceDurationRequest,
  UpdateActivityPedagogicalCustomFieldRequest,
} from '@api';

import type { GenerateStickerOptions } from '../models/generate-sticker-options.model';
import { ActivitiesListApiService, GetActivitiesOptions } from './api/activities.api.service';
import { ActivityFavoritesApiService } from './api/activity-favorites.api.service';
import { ActivityInstancesApiService } from './api/activity-instances.api.service';
import { ActivityApiService } from './api/activity.api.service';
import { TagsApiService } from './api/tags.api.service';

export type ActivityDetailSource = 'instance' | 'template';

/** Backend `activityType` values on unified list items. */
export const ACTIVITY_TYPE_TEMPLATE = 'Template';
export const ACTIVITY_TYPE_INSTANCE = 'Instance';

@Injectable({ providedIn: 'root' })
export class ActivityService {
  readonly #activityApiService = inject(ActivityApiService);
  readonly #activitiesListApiService = inject(ActivitiesListApiService);
  readonly #activityFavoritesApiService = inject(ActivityFavoritesApiService);
  readonly #activityInstancesApiService = inject(ActivityInstancesApiService);
  readonly #tagsApiService = inject(TagsApiService);
  readonly #topicsApiService = inject(TopicsApiService);

  getActivities(options?: GetActivitiesOptions): Promise<ActivitiesListResponse> {
    return this.#activitiesListApiService.list(options);
  }

  getActivityById(
    id: string,
    source: ActivityDetailSource = 'template',
  ): Promise<ActivityInstanceDetail | ActivityTemplateDetail> {
    if (source === 'instance') {
      return this.getActivityInstanceById(id);
    }

    return this.#activityApiService.getActivityTemplateById(id);
  }

  getActivityInstanceById(id: string): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.getById(id);
  }

  deleteMyActivity(id: string): Promise<void> {
    return this.#activityInstancesApiService.delete(id);
  }

  copyActivity(id: string, source: ActivityDetailSource = 'template'): Promise<string> {
    if (source === 'instance') {
      return this.#activityInstancesApiService.copy(id);
    }

    return this.#activityApiService.copy(id);
  }

  createEmpty(): Promise<string> {
    return this.#activityInstancesApiService.createEmpty();
  }

  updateName(id: string, value: string): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateName(id, value);
  }

  updateDescription(id: string, value: string): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateDescription(id, value);
  }

  updateVisibility(id: string, visibility: ActivityVisibility): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateVisibility(id, visibility);
  }

  updateInstructions(id: string, value: string[]): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateInstructions(id, value);
  }

  updateSections(id: string, value: ActivityContentSection[]): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateSections(id, value);
  }

  updateFieldLayout(id: string, value: ActivityFieldLayoutItem[]): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateFieldLayout(id, value);
  }

  updateDuration(id: string, duration: UpdateActivityInstanceDurationRequest): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateDuration(id, duration);
  }

  updateTags(id: string, tagIds: string[]): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateTags(id, tagIds);
  }

  updateSubTopic(id: string, subTopicId: string): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.updateSubTopic(id, subTopicId);
  }

  // Links a batch of Kontextus documents to an owned activity instance (KT-200.4, contract C10).
  // Delegates to the instances api-service adapter — the same service→api seam every other
  // mutation uses. This is an immediate POST, NOT the debounced `commitField` field-save path.
  linkContextDocuments(id: string, documentIds: string[]): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.linkContextDocuments(id, documentIds);
  }

  // Unlinks a single Kontextus document from an owned activity instance (KT-200.4, contract C10).
  // Delegates to the instances api-service adapter; an immediate DELETE, not `commitField`.
  unlinkContextDocument(id: string, documentId: string): Promise<ActivityInstanceDetail> {
    return this.#activityInstancesApiService.unlinkContextDocument(id, documentId);
  }

  generateSticker(id: string, options?: GenerateStickerOptions): Promise<ActivityStickerGeneration> {
    return this.#activityInstancesApiService.generateSticker(id, options);
  }

  generateCover(id: string, options?: { signal?: AbortSignal }): Promise<ActivityCoverGenerationResult> {
    return this.#activityInstancesApiService.generateCover(id, options);
  }

  updateCoverFraming(id: string, framing: UpdateActivityCoverFramingRequest): Promise<ActivityCover> {
    return this.#activityInstancesApiService.updateCoverFraming(id, framing);
  }

  getPedagogicalFields(id: string): Promise<ActivityPedagogicalFieldList> {
    return this.#activityInstancesApiService.getPedagogicalFields(id);
  }

  updatePedagogicalFieldValue(
    id: string,
    fieldName: PedagogicalFieldName,
    value: string | null,
  ): Promise<ActivityPedagogicalFieldResponse> {
    return this.#activityInstancesApiService.updatePedagogicalFieldValue(id, fieldName, value);
  }

  updatePedagogicalFieldVisibility(
    id: string,
    fieldName: PedagogicalFieldName,
    visible: boolean,
  ): Promise<ActivityPedagogicalFieldList> {
    return this.#activityInstancesApiService.updatePedagogicalFieldVisibility(id, fieldName, visible);
  }

  reorderPedagogicalField(
    id: string,
    fieldName: PedagogicalFieldName,
    position: number,
  ): Promise<ActivityPedagogicalFieldList> {
    return this.#activityInstancesApiService.reorderPedagogicalField(id, fieldName, position);
  }

  getPedagogicalCustomFields(id: string): Promise<ActivityPedagogicalCustomFieldList> {
    return this.#activityInstancesApiService.getPedagogicalCustomFields(id);
  }

  createPedagogicalCustomField(
    id: string,
    body: CreateActivityPedagogicalCustomFieldRequest,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return this.#activityInstancesApiService.createPedagogicalCustomField(id, body);
  }

  updatePedagogicalCustomFieldVisibility(
    id: string,
    fieldId: string,
    visible: boolean,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return this.#activityInstancesApiService.updatePedagogicalCustomFieldVisibility(id, fieldId, visible);
  }

  updatePedagogicalCustomField(
    id: string,
    fieldId: string,
    body: UpdateActivityPedagogicalCustomFieldRequest,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return this.#activityInstancesApiService.updatePedagogicalCustomField(id, fieldId, body);
  }

  reorderPedagogicalCustomField(
    id: string,
    fieldId: string,
    position: number,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return this.#activityInstancesApiService.reorderPedagogicalCustomField(id, fieldId, position);
  }

  updatePedagogicalCustomFieldValue(
    id: string,
    fieldId: string,
    value: string | null,
  ): Promise<ActivityPedagogicalCustomFieldList> {
    return this.#activityInstancesApiService.updatePedagogicalCustomFieldValue(id, fieldId, value);
  }

  deletePedagogicalCustomField(id: string, fieldId: string): Promise<ActivityPedagogicalCustomFieldList> {
    return this.#activityInstancesApiService.deletePedagogicalCustomField(id, fieldId);
  }

  getAttachmentContentUrl(attachmentId: string, mode: AttachmentMode): string {
    return this.#activityApiService.getAttachmentContentUrl(attachmentId, mode);
  }

  setFavorite(activityId: string): Promise<void> {
    return this.#activityFavoritesApiService.setFavorite(activityId);
  }

  removeFavorite(activityId: string): Promise<void> {
    return this.#activityFavoritesApiService.removeFavorite(activityId);
  }

  getTags(): Promise<GetTagsItem[]> {
    return this.#tagsApiService.getTags();
  }

  getTopics(): Promise<TopicItem[]> {
    return this.#topicsApiService.getTopics();
  }
}
